import React, { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import axios from 'axios';
import { useSelector } from 'react-redux';

const CameraChart = () => {
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const date = useSelector((state) => state.customization.currentDate);
  const newDate = new Date(date);
  const formattedDate = newDate.toISOString().split('T')[0];

  const evidenceTypeConfig = {
    corruption: {
      fillColor: '#ef4444',
      typeLabel: 'Corruption',
      statusText: 'Video corruption detected'
    },
    noConnection: {
      fillColor: '#f97316',
      typeLabel: 'No Connection',
      statusText: 'Camera connection lost'
    },
    default: {
      fillColor: '#3b82f6',
      typeLabel: 'Normal Operation',
      statusText: 'No faults detected'
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const processChartData = (apiResponse, selectedDate) => {
    const series = [];
    const dateObj = new Date(selectedDate);
    const dayStart = new Date(dateObj.setHours(0, 0, 0, 0));
    const dayEnd = new Date(dateObj.setHours(23, 59, 59, 999));

    const allDeviceNames = apiResponse.map((device) => device.device_name);

    allDeviceNames.forEach((deviceName) => {
      const device = apiResponse.find((d) => d.device_name === deviceName) || {
        device_name: deviceName,
        evidences: []
      };

      // Sort evidences by type (noConnection first, then corruption)
      const sortedEvidences = [...device.evidences].sort((a, b) => {
        if (a.type === 'noConnection' && b.type !== 'noConnection') return -1;
        if (a.type !== 'noConnection' && b.type === 'noConnection') return 1;
        return 0;
      });

      const deviceData = {
        name: device.device_name,
        data: [
          {
            x: device.device_name,
            y: [dayStart.getTime(), dayEnd.getTime()],
            fillColor: '#3b82f6',
            meta: {
              type: 'normal',
              status: 'No faults detected'
            }
          }
        ]
      };

      // Track time ranges to avoid overlaps
      const timeRanges = [];

      sortedEvidences.forEach((evidence) => {
        const startTime = new Date(evidence.start_time).getTime();
        const endTime = new Date(evidence.end_time).getTime();

        // Check if this evidence overlaps with any existing time range
        const isOverlapping = timeRanges.some((range) => startTime < range.end && endTime > range.start);

        if (!isOverlapping) {
          timeRanges.push({ start: startTime, end: endTime });

          const config = evidenceTypeConfig[evidence.type] || evidenceTypeConfig.default;

          deviceData.data.push({
            x: device.device_name,
            y: [startTime, endTime],
            fillColor: config.fillColor,
            meta: {
              type: evidence.type,
              typeLabel: config.typeLabel,
              status: config.statusText,
              startTime: formatTime(startTime),
              endTime: formatTime(endTime)
            }
          });
        }
      });

      series.push(deviceData);
    });

    return series;
  };

  const renderChart = (data) => {
    if (!chartRef.current) return;

    const options = {
      series: processChartData(data, formattedDate),
      chart: {
        height: 350,
        type: 'rangeBar',
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '80%',
          rangeBarGroupRows: true
        }
      },
      xaxis: {
        type: 'datetime',
        min: new Date(`${formattedDate}T00:00:00`).toISOString(),
        max: new Date(`${formattedDate}T23:59:59`).toISOString(),
        labels: {
          format: 'HH:mm',
          formatter: function (value) {
            // Convert UTC timestamp to IST (UTC+5:30)
            const date = new Date(value);
            return date.toLocaleTimeString('en-IN', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
              timeZone: 'Asia/Kolkata'
            });
          }
        }
      },
      yaxis: {
        title: {
          text: 'Devices'
        },
        categories: data.map((device) => device.device_name),
        forceNiceScale: true,
        show: true,
        hideOverlappingLabels: false
      },
      tooltip: {
        custom: function ({ seriesIndex, dataPointIndex, w }) {
          const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
          const start = data.meta.startTime;
          const end = data.meta.endTime;
          const duration = (data.y[1] - data.y[0]) / 1000 / 60;
          const hours = Math.floor(duration / 60);
          const minutes = Math.round(duration % 60);

          const meta = data.meta || {};
          const typeLabel = meta.typeLabel || 'Normal Operation';
          const status = meta.status || 'No faults detected';

          const bgColor = meta.type ? (evidenceTypeConfig[meta.type] || evidenceTypeConfig.default).fillColor : '#3b82f6';

          return `
            <div class="p-3 rounded-lg shadow-lg" style="background-color: ${bgColor}; color: white;">
              <div class="mb-1">${start ? start : 'N/A'} - ${end ? end : 'N/A'}</div>
              <div class="font-bold text-lg mb-1">${w.globals.labels[seriesIndex]}</div>
              <div class="mb-1">Duration: ${hours > 0 ? `${hours} hr${hours !== 1 ? 's' : ''} ` : ''}${minutes} min${
            minutes !== 1 ? 's' : ''
          }</div>              <div class="border-t border-white pt-1 mt-1">
                <div>Type: ${typeLabel}</div>
                <div>Status: ${status}</div>
              </div>
            </div>
          `;
        }
      },
      legend: {
        position: 'top',
        show: false,
        horizontalAlign: 'left',
        markers: {
          fillColors: ['#3b82f6', '#ef4444', '#f97316']
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5
        }
      },
      colors: ['#3b82f6', '#ef4444', '#f97316']
    };

    if (chartRef.current.chart) {
      chartRef.current.chart.destroy();
    }

    chartRef.current.chart = new ApexCharts(chartRef.current, options);
    chartRef.current.chart.render();
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.post('https://neoe2e.neophyte.live/fm-api/v2/camera_evidence', {
          date: formattedDate
        });

        renderChart(response.data);
      } catch (err) {
        console.error('Error:', err);
        setError(err.message || 'Failed to load camera evidence data');
      } finally {
        setLoading(false);
      }
    };

    if (formattedDate && chartRef.current) {
      fetchData();
    }

    return () => {
      if (chartRef.current?.chart) {
        chartRef.current.chart.destroy();
      }
    };
  }, [formattedDate]);

  return (
    <div className="p-4 bg-white rounded-lg shadow">
      <h2 className="text-2xl font-bold mb-4 text-gray-800">Camera Evidence Timeline - {formattedDate}</h2>

      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading camera data ...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded mb-4">
          <div className="flex items-center">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-semibold">Error:</span>
          </div>
          <p className="mt-1">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition"
          >
            Retry
          </button>
        </div>
      )}

      <div id="camera-evidence-chart" ref={chartRef} className="mt-6" style={{ minHeight: '350px' }} />
    </div>
  );
};

export default CameraChart;
